import { useEffect, useState } from 'react'
import { useTranslation } from '@lb/i18n'

/**
 * The install affordance.
 *
 * ── Why the button only sometimes exists ──────────────────────────────────
 * `beforeinstallprompt` fires only when the browser has decided the app is
 * installable and has not been installed already. There is no way to ask. So
 * this renders nothing until the event arrives, rather than showing a button
 * that would have to explain why it does nothing — which is what a
 * permanently-visible "Install" does on iOS, where the event never fires at all.
 *
 * ── Installability yes, offline no ────────────────────────────────────────
 * `Architecture_And_Plan.md` §12 took that decision and gave the reason: signed
 * media URLs expire (§4.8), and a cached response that outlives its signature
 * fails in the least debuggable way. So this is a home-screen shortcut and
 * there is no service worker behind it. The copy says so, because "install"
 * otherwise implies an offline mode that does not exist.
 */

/** The event is still non-standard, so its shape is declared rather than imported. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function InstallPrompt() {
  const { t } = useTranslation()
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    const onPrompt = (event: Event) => {
      // Chrome shows its own mini-infobar otherwise, at a moment of its
      // choosing rather than ours.
      event.preventDefault()
      setDeferred(event as BeforeInstallPromptEvent)
    }
    // Installed from elsewhere — the browser menu, another tab. The affordance
    // must disappear, not linger and fail.
    const onInstalled = () => setDeferred(null)

    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  if (!deferred) return null

  return (
    <button
      onClick={() => {
        void deferred.prompt()
        /*
         * Cleared either way. The event can only be used once — offering it a
         * second time does nothing, and a button that silently stops working is
         * worse than one that goes away.
         */
        setDeferred(null)
      }}
      className="flex min-h-[44px] w-full flex-col items-start rounded-[var(--r-sm)] px-3 py-2 text-left hover:bg-cream-dark"
    >
      <span className="text-[13px] font-semibold text-ink">{t('nav.install')}</span>
      <span className="text-[11px] text-muted">{t('nav.installWhy')}</span>
    </button>
  )
}
